// imports at top of that config file
// adjust path
import type { CreativeKey, TaskType, TaskValue } from './taskTypes' // you added earlier

// helper from your existing code
const withSubs = (base: TaskType, subs: string[] | undefined, labelPrefix?: string): TaskValue[] => {
  if (!subs || subs.length === 0) return [{ base, label: labelPrefix ?? base }]
  return subs.map(s => ({ base, sub: s, label: labelPrefix ? `${labelPrefix} — ${s}` : `${base} — ${s}` }))
}

/**
 * Minimal, sane creative bases (add/rename to taste).
 * - We keep these generic so they work across multiple creative types.
 * - Subs are examples; tweak freely.
 */
const CREATIVE_BASES = {
  creativeIntake: [] as string[],
  concept: ['Moodboard', 'Direction'],
  design: ['Initial', 'Refinement'],
  proofs: ['Round 1', 'Round 2', 'Final Proof'],
  revisions: ['Minor', 'Major'],
  assetHandoff: ['Drive Link', 'Brand Kit Update'],
  finalDelivery: ['Source Files', 'Exports'],
}

/** keys: CreativeKey (e.g., 'logoDesign', 'fullBrandBuild', 'website'...) */
export const CREATIVE_TASK_CATALOG: Partial<Record<CreativeKey, TaskValue[]>> = {
  // Logo Design
  logoDesign: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', CREATIVE_BASES.concept, 'Concept'),
    ...withSubs('design', CREATIVE_BASES.design, 'Design'),
    ...withSubs('proofs', CREATIVE_BASES.proofs, 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('assetHandoff', CREATIVE_BASES.assetHandoff, 'Asset Handoff'),
    ...withSubs('finalDelivery', CREATIVE_BASES.finalDelivery, 'Final Delivery'),
  ],

  // Full Brand Build
  fullBrandBuild: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', ['Brand Positioning', 'Visual Direction'], 'Concept'),
    ...withSubs('design', ['Logo', 'Visual ID', 'Collateral'], 'Design'),
    ...withSubs('proofs', CREATIVE_BASES.proofs, 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('assetHandoff', ['Brand Guide', 'Package'], 'Asset Handoff'),
    ...withSubs('finalDelivery', ['Guidelines', 'Exports'], 'Final Delivery'),
  ],

  // Website
  website: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', ['Sitemap', 'Wireframe'], 'Concept'),
    ...withSubs('design', ['Homepage', 'Interior Pages'], 'Design'),
    ...withSubs('proofs', CREATIVE_BASES.proofs, 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('assetHandoff', ['Figma', 'Assets'], 'Asset Handoff'),
    ...withSubs('finalDelivery', ['Spec / Export'], 'Final Delivery'),
  ],

  // Print Design
  printDesign: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', CREATIVE_BASES.concept, 'Concept'),
    ...withSubs('design', ['Layout', 'Production'], 'Design'),
    ...withSubs('proofs', ['Digital Proof', 'Press Proof'], 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Print-Ready Files'], 'Final Delivery'),
  ],
  // Design Materials
  designMaterials: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', CREATIVE_BASES.concept, 'Concept'),
    ...withSubs('design', ['Layout', 'Production'], 'Design'),
    ...withSubs('proofs', ['Digital Proof', 'Press Proof'], 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Print-Ready Files'], 'Final Delivery'),
  ],
  marketingCollateral: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', CREATIVE_BASES.concept, 'Concept'),
    ...withSubs('design', ['Layout', 'Production'], 'Design'),
    ...withSubs('proofs', ['Digital Proof', 'Press Proof'], 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Print-Ready Files'], 'Final Delivery'),
  ],

  // Photography
  photography: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', ['Shot List', 'Locations'], 'Concept'),
    ...withSubs('design', ['Shoot'], 'Production'),
    ...withSubs('proofs', ['Contact Sheet'], 'Proofs'),
    ...withSubs('revisions', ['Selects'], 'Revisions'),
    ...withSubs('finalDelivery', ['RAW/Processed', 'Exports'], 'Final Delivery'),
  ],

  // Video
  video: [
    ...withSubs('creativeIntake', CREATIVE_BASES.creativeIntake, 'Creative Intake'),
    ...withSubs('concept', ['Treatment', 'Script'], 'Concept'),
    ...withSubs('design', ['Production', 'Edit'], 'Production'),
    ...withSubs('proofs', ['Cut 1', 'Cut 2', 'Final Cut'], 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Masters', 'Exports'], 'Final Delivery'),
  ],

  // Social creatives (if you treat these as creative products)
  socialAds: [
    ...withSubs('creativeIntake', [], 'Creative Intake'),
    ...withSubs('design', ['Static', 'Carousel', 'Video'], 'Design'),
    ...withSubs('proofs', CREATIVE_BASES.proofs, 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Exports'], 'Final Delivery'),
  ],

  displayAds: [
    ...withSubs('creativeIntake', [], 'Creative Intake'),
    ...withSubs('design', ['Standard Set', 'Responsive Set'], 'Design'),
    ...withSubs('proofs', CREATIVE_BASES.proofs, 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Exports'], 'Final Delivery'),
  ],

  organicSocial: [
    ...withSubs('creativeIntake', [], 'Creative Intake'),
    ...withSubs('design', ['Post Set'], 'Design'),
    ...withSubs('proofs', CREATIVE_BASES.proofs, 'Proofs'),
    ...withSubs('revisions', CREATIVE_BASES.revisions, 'Revisions'),
    ...withSubs('finalDelivery', ['Exports'], 'Final Delivery'),
  ],
}
export const creativeTaskTypeTreeFor = (key: CreativeKey): Record<string, string[]> => {
  const values = CREATIVE_TASK_CATALOG[key] ?? []
  const tree: Record<string, string[]> = {}
  for (const v of values) {
    if (!tree[v.base]) tree[v.base] = []
    if (v.sub) tree[v.base].push(v.sub)
  }
  for (const k of Object.keys(tree)) {
    tree[k] = Array.from(new Set(tree[k]))
  }
  return tree
}
export const photoKeys = [
  {
    key: 'photo_shoot-1',
    label: 'Schedule Shoot',
  },
  {
    key: 'photo_shoot-2',
    label: 'Shoot',
  },
  {
    key: 'photo_shoot-3',
    label: 'Transfer Footage',
  },
  {
    key: 'photo_shoot-4',
    label: 'Culling Photos',
  },
  {
    key: 'photo_shoot-5',
    label: 'Send to AM/PM/BDE',
  },
  {
    key: 'photo_shoot-6',
    label: 'Show up/Set up',
  },
  {
    key: 'photo_shoot-7',
    label: 'Shoot Prep',
  },
  {
    key: 'photo_shoot-8',
    label: 'Book Models',
  },
  {
    key: 'photo_shoot-9',
    label: 'Edit',
  },
  {
    key: 'photo_shoot-10',
    label: 'Travel/Acommodations (if applicable)',
  },
]
export const createKeysFn = array => {
  return array.map(data => {
    return {
      key: data?.key,
      label: data?.name,
    }
  })
}
export const logoKeys = [
  { key: 'namingBrainstorming', label: 'Naming/Brainstorming' },
  { key: 'firstRoundLogoCreationMultipleDesigns', label: 'First Round Logo Creation (Multiple Designs)' },
  { key: 'creativePanelReview', label: 'Creative Panel Review' },
  { key: 'reviewSelections', label: 'Review Selections' },
  { key: 'brandPDFCreation', label: 'Brand PDF Creation' },
  { key: 'clientReview', label: 'Client Review' },
  { key: 'clientFeedbackRevisions', label: 'Client Feedback/Revisions' },
  { key: 'reviseBrandPDF', label: 'Revise Brand PDF' },
  { key: 'clientReviewRevisions', label: 'Client Review (After Revisions)' },
]
export const brandingGuideKeys = [
  {
    key: 'assembleWorkIntoBrandTemplate',
    label: 'Assemble work into Brand Template',
  },
  {
    key: 'brandMessaging',
    label: 'Brand Messaging',
  },
  {
    key: 'brandPatterns',
    label: 'Brand Patterns',
  },
  {
    key: 'brandPhotography',
    label: 'Brand Photography',
  },
]
export const fullBrandKeys = [
  {
    key: 'namingBrainstorming',
    label: 'Naming/Brainstorming',
  },
  {
    key: 'firstRoundLogoCreationMultipleDesigns',
    label: 'First Round Logo Creation (Multiple Designs)',
  },
  {
    key: 'creativePanelReview',
    label: 'Creative Panel Review',
  },
  {
    key: 'reviewSelections',
    label: 'Review Selections',
  },
  {
    key: 'brandPDFCreation',
    label: 'Brand PDF Creation',
  },
  {
    key: 'clientReview',
    label: 'Client Review',
  },
  {
    key: 'clientFeedbackRevisions',
    label: 'Client Feedback/Revisions',
  },
  {
    key: 'reviseBrandPDF',
    label: 'Revise Brand PDF',
  },
  {
    key: 'clientReviewFinal',
    label: 'Client Review (Final)',
  },
]
export const photoVideoTravelKeys = [
  {
    key: 'scheduleShoot',
    label: 'Schedule Shoot',
  },
  {
    key: 'travelAcommodationsIfApplicable',
    label: 'Travel/Acommodations (if applicable)',
  },
  {
    key: 'bookModels',
    label: 'Book Models\t',
  },
  {
    key: 'shootPrep',
    label: 'Shoot Prep\t',
  },
  {
    key: 'showUpSetUp',
    label: 'Show up/Set up',
  },
  {
    key: 'shoot',
    label: 'Shoot\t',
  },
  {
    key: 'transferFootage',
    label: 'Transfer Footage',
  },
  {
    key: 'cullingPhotos',
    label: 'Culling Photos',
  },
  {
    key: 'edit',
    label: 'Edit',
  },
  {
    key: 'sendToAMPMBDE',
    label: 'Send to AM/PM/BDE',
  },
]
export const photoVideoLocalKeys = [
  {
    key: 'scheduleShoot',
    label: 'Schedule Shoot',
  },
  {
    key: 'showUpSetUp',
    label: 'Show up/Set up',
  },
  {
    key: 'shootPrepShoot',
    label: 'Shoot Prep\tShoot',
  },
  {
    key: 'transferFootageEdit',
    label: 'Transfer Footage/Edit',
  },
  {
    key: 'sendToAMPMBDE',
    label: 'Send to AM/PM/BDE',
  },
]
export const equipmentKeys = [
  {
    key: 'bothCameras',
    label: 'Both Cameras',
  },
  {
    key: 'cameraBags',
    label: 'Camera Bags',
  },
  {
    key: 'memoryCardsWiped',
    label: 'Memory Cards wiped',
  },
  {
    key: 'allBatteriesCharged',
    label: 'All Batteries Charged',
  },
  {
    key: 'flash',
    label: 'Flash',
  },
  {
    key: 'extraBatteriesForFlash',
    label: 'Extra Batteries for Flash',
  },
  {
    key: 'shotListLocationIdeas',
    label: 'Shot List/Location/Ideas',
  },
  {
    key: 'tripodsIfNecessary',
    label: 'Tripods if necessary',
  },
  {
    key: 'lightequipmentIfNecessary',
    label: 'Light/equipment if necessary',
  },
  {
    key: 'drone',
    label: 'Drone',
  },
  {
    key: 'droneBatteriesCharged',
    label: 'Drone batteries Charged',
  },
  {
    key: 'droneUpdated',
    label: 'Drone updated',
  },
  {
    key: 'controllerCharged',
    label: 'Controller Charged',
  },
  {
    key: 'memoryCardWiped',
    label: 'Memory Card Wiped',
  },
]
export const premiumWebKeys = [
  {
    key: 'domainAcquisition',
    label: 'Domain Acquisition',
  },
  {
    key: 'contentVerification',
    label: 'Content Verification',
  },
  {
    key: 'setupDevSite',
    label: 'Setup Dev Site',
  },
  {
    key: 'homepageBuild',
    label: 'Homepage Build',
  },
  {
    key: 'homepageRevisions',
    label: 'Homepage Revisions',
  },
  {
    key: 'subpagesBuild',
    label: 'Subpages Build',
  },
  {
    key: 'subpageRevisions',
    label: 'Subpage Revisions',
  },
  {
    key: 'qualityAssuranceChecks',
    label: 'Quality Assurance Checks',
  },
  {
    key: 'cutoverToLiveDomain',
    label: 'Cutover to live domain',
  },
]
export const newWebKeys = [
  {
    key: 'key_1',
    label: 'Domain Acquisition',
  },
  {
    key: 'key_2',
    label: 'Content Verification',
  },
  {
    key: 'key_3',
    label: 'Setup Live Domain for Development',
  },
  {
    key: 'key_4',
    label: 'Homepage + Coming Soon page Build',
  },
  {
    key: 'key_5',
    label: 'Subpage Revisions',
  },
  {
    key: 'key_6',
    label: 'Subpages Build',
  },
  {
    key: 'key_7',
    label: 'Subpage Revisions',
  },
  {
    key: 'key_8',
    label: 'Quality Assurance Checks',
  },
  {
    key: 'key_9',
    label: 'Cutover to live domain',
  },
]
export const collateralKeys = [
  {
    key: 'mockupEnvelopes',
    label: 'Envelopes',
  },
  {
    key: 'mockupBusinessCards',
    label: 'Business Cards',
  },
  {
    key: 'mockupLetterhead',
    label: 'Letterhead',
  },
  {
    key: 'mockupSocialCoverPhotoFBXLinkedin',
    label: 'Social Cover Photo (FB, X, Linkedin)',
  },
  {
    key: 'mockupBusinessProfilePictureForSocial',
    label: 'Business Profile Picture for Social',
  },
]
